import type { Locale } from "./supported";
import { DEFAULT_LOCALE } from "./supported";
import type { ContentTranslation, TranslationStatus } from "./contentTranslations";

/**
 * Result of `pickLocalized`. `source` is true when the Swedish original was
 * returned (no translation matched). Render code can use this to decide
 * e.g. whether to render `<span lang="sv">` around the fallback so screen
 * readers switch pronunciation correctly when the active locale is `en`.
 */
export interface PickLocalizedResult {
  value: string;
  source: boolean;
  status: TranslationStatus | null;
}

interface PickLocalizedOptions {
  /**
   * Status values considered acceptable. Defaults to `['approved']` — the
   * public-render policy. Editor previews should pass `['approved','draft']`
   * or `['approved','draft','stale']`.
   */
  acceptStatuses?: readonly TranslationStatus[];
}

const DEFAULT_ACCEPT: readonly TranslationStatus[] = ["approved"];

/**
 * Picks the right value to render for a localized field.
 *
 * Returns the translation matching `locale` + `fieldName` + an accepted
 * status when present. Falls back to the Swedish `source` otherwise.
 *
 * Contract:
 *   - If `locale === DEFAULT_LOCALE` ('sv'), translations are not consulted
 *     and the source is returned. This is the cheap path on the default
 *     render — no array scans on Swedish pages.
 *   - If `source` is empty, the function still returns `''` and `source: true`
 *     so call sites can render their own empty-state rather than the key.
 *   - Translations whose status is not in `acceptStatuses` are ignored.
 *
 * See `docs/SoT/localization.md` §5.5.
 */
export function pickLocalized(
  source: string,
  translations: readonly ContentTranslation[] | null | undefined,
  fieldName: string,
  locale: Locale,
  options?: PickLocalizedOptions,
): PickLocalizedResult {
  if (locale === DEFAULT_LOCALE || !translations || translations.length === 0) {
    return { value: source ?? "", source: true, status: null };
  }

  const accept = options?.acceptStatuses ?? DEFAULT_ACCEPT;

  for (const t of translations) {
    if (t.field_name !== fieldName) continue;
    if (t.locale !== locale) continue;
    if (!accept.includes(t.status)) continue;
    return { value: t.value, source: false, status: t.status };
  }

  return { value: source ?? "", source: true, status: null };
}
